import React, { useCallback, useEffect, useMemo } from 'react';

import { useForm, useWatch } from 'react-hook-form';
import dayjs from '~/timeInit';

import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack } from '@mui/material';

import { DateTimeInput, Input, Select } from '~/components/Common/Forms/fields';

import { useCreatePointsPurchaseOffer } from '~/hooks/apis/loyalty/usePointsPurchaseMutations';

import getAllCurrencyCodes from '~/utils/getAllCurrencyCodes';

interface CreateOfferDialogProps {
  open: boolean;
  onClose: () => void;
  defaultCurrency: string;
}

interface CreateOfferFormData {
  title: string;
  basePoints: string;
  price: string;
  currency: string;
  validFrom: string;
}

function buildDefaultValues(currency: string): CreateOfferFormData {
  return {
    title: '',
    basePoints: '',
    price: '',
    currency,
    validFrom: dayjs().format('YYYY-MM-DDTHH:mm'),
  };
}

export default function CreateOfferDialog({ open, onClose, defaultCurrency }: CreateOfferDialogProps) {
  const createMutation = useCreatePointsPurchaseOffer();

  const defaultValues = useMemo(() => buildDefaultValues(defaultCurrency), [defaultCurrency]);

  const { control, handleSubmit, reset } = useForm<CreateOfferFormData>({
    defaultValues,
    mode: 'onBlur',
  });

  const selectedCurrency = useWatch({ control, name: 'currency' });

  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
  }, [open, reset, defaultValues]);

  const onSubmit = useCallback(
    async (data: CreateOfferFormData) => {
      try {
        await createMutation.mutateAsync({
          title: data.title.trim(),
          basePoints: Number(data.basePoints),
          price: Number(data.price),
          currency: data.currency,
          validFrom: dayjs(data.validFrom).toISOString(),
        });
        onClose();
      } catch {
        // Error handled in mutation hook
      }
    },
    [createMutation, onClose],
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Points Purchase Offer</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Input
            control={control}
            name="title"
            muiLabel="Title"
            rules={{ required: 'Title is required' }}
            fullWidth
            required
          />
          <Input
            control={control}
            name="basePoints"
            muiLabel="Base Points"
            type="number"
            rules={{
              required: 'Base points is required',
              validate: (v: string) => {
                const n = Number(v);
                if (!Number.isInteger(n)) return 'Must be a whole number';
                if (n <= 0) return 'Must be greater than 0';
                return true;
              },
            }}
            fullWidth
            required
          />
          <Input
            control={control}
            name="price"
            muiLabel="Price"
            type="number"
            rules={{
              required: 'Price is required',
              validate: (v: string) => {
                const n = Number(v);
                if (!Number.isInteger(n)) return 'Must be a whole number';
                if (n <= 0) return 'Must be greater than 0';
                return true;
              },
            }}
            fullWidth
            required
            helperText="Price and base points cannot be changed after creation"
          />
          <Select control={control} name="currency" muiLabel="Currency" rules={{ required: 'Currency is required' }}>
            {getAllCurrencyCodes().map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
          {selectedCurrency && selectedCurrency !== 'AUD' && (
            <Alert severity="info">
              Creating offers in {selectedCurrency} means customers viewing in {selectedCurrency} will see only these
              native offers instead of AUD offers with FX conversion. This is all-or-nothing per currency — if even one{' '}
              {selectedCurrency} offer exists, AUD offers will not be shown to {selectedCurrency} viewers.
            </Alert>
          )}
          <DateTimeInput
            control={control}
            name="validFrom"
            label="Valid From"
            rules={{ required: 'Valid from date is required' }}
            fullWidth
            required
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={createMutation.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={createMutation.isPending}>
          {createMutation.isPending ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
